import { Box, CircularProgress } from '@mui/material'
import { useContext, useEffect, useState } from 'react'

import {
  PatientsContext,
  PatientsContextInterface,
} from '../../../providers/Patients/Patients.provider'
import { useProviderDetails } from '../../../providers/ProviderDetails/ProviderDetails.provider'
import { useProviders } from '../../../providers/ProvidersProvider/ProvidersProvider.provider'
import {
  ProviderTaskBarrier,
  ProviderTaskContext,
  ProviderTaskContextInterface,
  ProviderTaskPriorityLevel,
  ProviderTaskResponse,
  ProviderTaskStatus,
  ProviderTaskType,
} from '../../../providers/ProviderTask/ProviderTask.provider'
import { ProviderTasksView } from './ProviderTasks.view'

const ProviderTasks = () => {
  const {
    providerTasks,
    providerTaskHistory,
    getProviderTaskTypes,
    getProviderTaskPriorityLevels,
    getProviderTaskStatuses,
    getProviderTaskBarriers,
    getAllProviderTasks,
    getProviderTasksByProviderId,
    createProviderTaskType,
    createProviderTask,
    updateProviderTask,
    deleteProviderTask,
    addProviderTaskComment,
    editProviderTaskComment,
    deleteProviderTaskComment,
    getProviderTaskHistory,
  } = useContext(ProviderTaskContext) as ProviderTaskContextInterface

  const { myProviderDetails } = useProviderDetails()

  const { allActivePatients, getListOfAllActivePatients } = useContext(
    PatientsContext
  ) as PatientsContextInterface

  const { providers, getAllProviders } = useProviders()

  const [taskTypes, setTaskTypes] = useState<ProviderTaskType[]>([])
  const [priorityLevels, setPriorityLevels] = useState<
    ProviderTaskPriorityLevel[]
  >([])
  const [statuses, setStatuses] = useState<ProviderTaskStatus[]>([])
  const [barriers, setBarriers] = useState<ProviderTaskBarrier[]>([])
  const [loading, setLoading] = useState(true)

  const [optimisticTasks, setOptimisticTasks] = useState<
    ProviderTaskResponse[]
  >([])

  const isAdmin = myProviderDetails?.accessRoles?.some(
    (role) =>
      role.description.includes('Admin') ||
      role.description.includes('SuperAdmin')
  )

  const loadData = async () => {
    setLoading(true)

    const [types, levels, statusList, barrierList] = await Promise.all([
      getProviderTaskTypes(),
      getProviderTaskPriorityLevels(),
      getProviderTaskStatuses(),
      getProviderTaskBarriers(),
    ])

    if (types) setTaskTypes(types)
    if (levels) setPriorityLevels(levels)
    if (statusList) setStatuses(statusList)
    if (barrierList) setBarriers(barrierList)

    if (isAdmin) {
      await getListOfAllActivePatients()
      await getAllProviders()
    } else if (myProviderDetails?.providerId) {
      await getProviderTasksByProviderId(myProviderDetails.providerId)
    }

    setLoading(false)
  }

  useEffect(() => {
    loadData()
  }, [])

  // make a copy of tasks, so that we can update local state later to reflect changes fast
  useEffect(() => {
    setOptimisticTasks(providerTasks ?? [])
  }, [providerTasks])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <ProviderTasksView
      isAdmin={!!isAdmin}
      tasks={optimisticTasks}
      setOptimisticTasks={setOptimisticTasks}
      providers={providers ?? []}
      allActivePatients={allActivePatients}
      myProviderId={myProviderDetails?.providerId ?? 0}
      taskTypes={taskTypes}
      setTaskTypes={setTaskTypes}
      priorityLevels={priorityLevels}
      statuses={statuses}
      barriers={barriers}
      getAllProviderTasks={getAllProviderTasks}
      getProviderTasksByProviderId={getProviderTasksByProviderId}
      onCreateTask={createProviderTask}
      onUpdateTask={updateProviderTask}
      onDeleteTask={deleteProviderTask}
      onCreateTaskType={createProviderTaskType}
      onAddProviderTaskComment={addProviderTaskComment}
      onEditProviderTaskComment={editProviderTaskComment}
      onDeleteProviderTaskComment={deleteProviderTaskComment}
      providerTaskHistory={providerTaskHistory}
      onFetchProviderTaskHistory={getProviderTaskHistory}
    />
  )
}

export { ProviderTasks }
